import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { Arbeitsbereich, Mitarbeiter } from '../../../entities';
import { faExclamationCircle } from '@fortawesome/pro-light-svg-icons';
import { useArbeitsbereiche } from '../../../hooks/useArbeitsbereiche';
import { useTranslation } from 'react-i18next';
import { Markierung } from '../../Markierung';
import {
  faAnglesUpDown,
  faFilter,
  faTimes,
} from '@fortawesome/pro-regular-svg-icons';
import { Button } from '@/components/ui/button';
import { Popover, PopoverTrigger } from '@/components/ui/popover';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { getColorByOption } from '@/domain/arbeitszeiten/components/Comboboxen/ArbeitsbereichCombobox/getColorByOption';
import { ArbeitsbereichComboboxPopoverContent } from '@/domain/arbeitszeiten/components/Comboboxen/ArbeitsbereichCombobox/ArbeitsbereichComboboxPopoverContent';
import { cn } from '@/lib/utils';

// eslint-disable-next-line max-lines-per-function, complexity
export function ArbeitsbereichFilter({
  onSelect,
  multiSelect = true,
  defaultValue = [],
  mitarbeiter,
  minimunSelect = 0,
  open,
  onOpenChange,
}: {
  onSelect: (arbeitsbereiche: Arbeitsbereich[]) => void;
  multiSelect?: boolean;
  defaultValue?: Arbeitsbereich[];
  mitarbeiter?: Mitarbeiter;
  minimunSelect?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const {
    currentStandort: { id: standortId },
  } = useStandortContext();
  const { arbeitsbereiche, error } = useArbeitsbereiche(standortId);
  const { t } = useTranslation();

  const [selectedArbeitsbereiche, setSelectedArbeitsbereiche] =
    useState<Arbeitsbereich[]>(defaultValue);

  useEffect(() => {
    if (!multiSelect && mitarbeiter && mitarbeiter.arbeitsbereich) {
      const mitarbeiterArbeitsbereich = mitarbeiter.arbeitsbereich;
      if (
        !selectedArbeitsbereiche.some(
          (ab) => ab.id === mitarbeiterArbeitsbereich.id,
        )
      ) {
        setSelectedArbeitsbereiche([mitarbeiterArbeitsbereich]);
        onSelect([mitarbeiterArbeitsbereich]);
      }
    }
  }, [mitarbeiter, multiSelect]);

  if (error) {
    return (
      <Button
        variant="ghost"
        role="combobox"
        aria-expanded={open}
        className="w-[240px] justify-between bg-white border border-neutrals-N300">
        {selectedArbeitsbereiche.length > 0 ? (
          <div className="flex gap-2 items-center">
            <Markierung
              color={getColorByOption(
                selectedArbeitsbereiche[selectedArbeitsbereiche.length - 1].id,
                arbeitsbereiche,
              )}
            />
            {selectedArbeitsbereiche[selectedArbeitsbereiche.length - 1].name}
          </div>
        ) : (
          t('domain.arbeitszeiten.uebersicht.arbeitsbereichfilter.placeholder')
        )}

        <FontAwesomeIcon
          icon={faExclamationCircle}
          className="text-primary-red-R500"
        />
      </Button>
    );
  }

  function handleReset() {
    setSelectedArbeitsbereiche([]);
    onSelect([]);
  }

  return (
    <>
      <div className="flex lg:hidden">
        <Popover
          open={open}
          onOpenChange={(newOpen) => {
            if (onOpenChange) {
              onOpenChange(newOpen);
            }
          }}>
          <PopoverTrigger asChild>
            <div className="relative">
              {selectedArbeitsbereiche.length > 0 && (
                <span className="absolute top-[5px] right-[5px] size-[5px] inline-block text-center font-semibold rounded-full bg-primary-blue-B600" />
              )}
              <Button variant="ghost" size="icon">
                <FontAwesomeIcon icon={faFilter} />
              </Button>
            </div>
          </PopoverTrigger>
          <ArbeitsbereichComboboxPopoverContent
            arbeitsbereiche={arbeitsbereiche}
            onSelect={onSelect}
            selectedArbeitsbereiche={selectedArbeitsbereiche}
            setSelectedArbeitsbereiche={setSelectedArbeitsbereiche}
            minimunSelect={minimunSelect}
            multiSelect={multiSelect}
            onOpenChange={onOpenChange}
          />
        </Popover>
      </div>
      <div className="hidden lg:inline">
        <Popover
          open={open}
          onOpenChange={(newOpen) => {
            if (onOpenChange) {
              onOpenChange(newOpen);
            }
          }}>
          <PopoverTrigger asChild>
            <Button
              role="combobox"
              aria-expanded={open}
              variant="outline-secondary"
              className={cn(
                'w-[275px] justify-between font-normal [&>svg]:text-neutrals-N900',
                selectedArbeitsbereiche
                  ? 'text-neutrals-N900'
                  : 'text-neutrals-N700',
              )}>
              {selectedArbeitsbereiche.length > 0 ? (
                <div className="flex gap-2 items-baseline">
                  <Markierung
                    color={getColorByOption(
                      selectedArbeitsbereiche[
                        selectedArbeitsbereiche.length - 1
                      ].id,
                      arbeitsbereiche,
                    )}
                  />
                  <span className="truncate w-[130px] text-start text-sm">
                    {
                      selectedArbeitsbereiche[
                        selectedArbeitsbereiche.length - 1
                      ].name
                    }
                  </span>
                  {selectedArbeitsbereiche.length > 1 && (
                    <span className="text-neutrals-N700">
                      {'+' + (selectedArbeitsbereiche.length - 1) + ' weitere'}
                    </span>
                  )}
                </div>
              ) : (
                <span className="w-40 font-normal text-neutrals-N700">
                  {t(
                    'domain.arbeitszeiten.uebersicht.arbeitsbereichfilter.placeholder',
                  )}
                </span>
              )}

              <div className="flex items-baseline ps-2">
                {selectedArbeitsbereiche.length > 0 && minimunSelect === 0 && (
                  <div
                    className="group"
                    onClick={(event) => {
                      event.preventDefault();
                      handleReset();
                    }}>
                    {!multiSelect && (
                      <FontAwesomeIcon
                        className="group-hover:text-primary-blue-B600 text-neutrals-N700"
                        size="lg"
                        icon={faTimes}
                      />
                    )}
                  </div>
                )}
                <FontAwesomeIcon icon={faAnglesUpDown} />
              </div>
            </Button>
          </PopoverTrigger>
          <ArbeitsbereichComboboxPopoverContent
            arbeitsbereiche={arbeitsbereiche}
            onSelect={onSelect}
            selectedArbeitsbereiche={selectedArbeitsbereiche}
            setSelectedArbeitsbereiche={setSelectedArbeitsbereiche}
            minimunSelect={minimunSelect}
            multiSelect={multiSelect}
            onOpenChange={onOpenChange}
          />
        </Popover>
      </div>
    </>
  );
}
